import React, { useEffect, useRef, useState } from 'react';
import { GameResultOverlay, GameShell, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;
const LEVEL_UP_STREAK = 2;

const createBalanceRound = (level = 1, trialIndex = 1) => {
    const maxMagnitude = Math.min(15, 5 + Math.floor(level / 2));
    let unknown = 1 + Math.floor(Math.random() * maxMagnitude);
    let known = 1 + Math.floor(Math.random() * maxMagnitude);

    if (level >= 3) {
        if (trialIndex % 2 === 1) {
            unknown *= -1;
        } else {
            known *= -1;
        }
    }

    const total = known + unknown;
    const values = new Set([unknown]);
    shuffle([-4, -3, -2, -1, 1, 2, 3, 4])
        .slice(0, 3)
        .forEach((offset) => values.add(unknown + offset));

    return {
        answer: unknown,
        id: `${trialIndex}-${level}-${Date.now()}`,
        known,
        unknownSide: trialIndex % 2 === 0 ? 'right' : 'left',
        options: shuffle([...values]),
        total,
    };
};

function BalanceBeamGame({
    copy = {},
    onRunComplete = null,
    roundLimit = null,
    showResults = true,
    initialLevel,
    ...props
}) {
    const startingLevel = normalizeBrainGameLevel('balance-beam', initialLevel);
    const timerRef = useRef(null);
    const completionReportedRef = useRef(false);
    const [roundIndex, setRoundIndex] = useState(1);
    const [level, setLevel] = useState(startingLevel);
    const [round, setRound] = useState(() => createBalanceRound(startingLevel, 1));
    const [choice, setChoice] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const normalizedRoundLimit = Number(roundLimit);
    const maxRounds = Number.isFinite(normalizedRoundLimit) && normalizedRoundLimit > 0
        ? Math.max(1, Math.round(normalizedRoundLimit))
        : null;
    const done = maxRounds !== null && roundIndex > maxRounds;
    const correct = choice !== null && choice === round.answer;
    const leftValues = round.unknownSide === 'left' ? [round.known, '?'] : [round.total];
    const rightValues = round.unknownSide === 'right' ? [round.known, '?'] : [round.total];
    const equationLabel = formatGameCopy(copy.balanceEquation, {
        left: leftValues.join(` ${copy.plus} `),
        right: rightValues.join(` ${copy.plus} `),
    });

    const metrics = {
        ...(maxRounds !== null ? { maxScore: maxRounds } : {}),
        level,
        score,
        streak,
    };

    const reset = () => {
        window.clearTimeout(timerRef.current);
        completionReportedRef.current = false;
        setRoundIndex(1);
        setLevel(startingLevel);
        setRound(createBalanceRound(startingLevel, 1));
        setChoice(null);
        setScore(0);
        setStreak(0);
    };

    useEffect(() => () => window.clearTimeout(timerRef.current), []);

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.(metrics);
    }, [done, level, maxRounds, onRunComplete, score, streak]);

    const choose = (value) => {
        if (done || choice !== null) {
            return;
        }

        const isCorrect = value === round.answer;
        const nextStreak = isCorrect ? streak + 1 : 0;
        const nextLevel = normalizeBrainGameLevel(
            'balance-beam',
            level + (isCorrect && nextStreak % LEVEL_UP_STREAK === 0 ? 1 : 0)
        );
        setChoice(value);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        timerRef.current = window.setTimeout(() => {
            const nextIndex = roundIndex + 1;
            setRoundIndex(nextIndex);
            setLevel(nextLevel);
            setChoice(null);
            if (maxRounds === null || nextIndex <= maxRounds) {
                setRound(createBalanceRound(nextLevel, nextIndex));
            }
        }, isCorrect ? 700 : 950);
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={metrics}
            tone={correct || done ? 'good' : choice !== null ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__balance-game">
                <div className="UFOV-brain-games__balance-scale" aria-label={equationLabel}>
                    <div className="UFOV-brain-games__balance-pan is-left">
                        <i aria-hidden="true" className="UFOV-brain-games__balance-suspension" />
                        <div className="UFOV-brain-games__balance-weights">
                            {leftValues.map((value, index) => (
                                <span
                                    className={[
                                        value === '?' ? 'is-unknown' : '',
                                        Number.isFinite(value) && value < 0 ? 'is-negative' : '',
                                    ].filter(Boolean).join(' ')}
                                    key={`left-${value}-${index}`}
                                >
                                    {value}
                                </span>
                            ))}
                        </div>
                        <i aria-hidden="true" className="UFOV-brain-games__balance-tray" />
                    </div>
                    <div className="UFOV-brain-games__balance-pan is-right">
                        <i aria-hidden="true" className="UFOV-brain-games__balance-suspension" />
                        <div className="UFOV-brain-games__balance-weights">
                            {rightValues.map((value, index) => (
                                <span
                                    className={[
                                        value === '?' ? 'is-unknown' : '',
                                        Number.isFinite(value) && value < 0 ? 'is-negative' : '',
                                    ].filter(Boolean).join(' ')}
                                    key={`right-${value}-${index}`}
                                >
                                    {value}
                                </span>
                            ))}
                        </div>
                        <i aria-hidden="true" className="UFOV-brain-games__balance-tray" />
                    </div>
                    <i aria-hidden="true" className="UFOV-brain-games__balance-beam" />
                    <i aria-hidden="true" className="UFOV-brain-games__balance-stand" />
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.balanceCue}</div>
                <div className="UFOV-brain-games__quality-options">
                    {round.options.map((option) => (
                        <button
                            className={[
                                choice === option ? 'is-picked' : '',
                                choice !== null && option === round.answer ? 'is-correct' : '',
                            ].filter(Boolean).join(' ')}
                            disabled={done || choice !== null}
                            key={`${round.id}-${option}`}
                            onClick={() => choose(option)}
                            type="button"
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>
            {done && showResults ? <GameResultOverlay metrics={metrics} onRestart={reset} /> : null}
        </GameShell>
    );
}

export default BalanceBeamGame;
